// Просмотр данных пользователя — строго на чтение.
//
// Планировщик здесь не поднимается намеренно: задача экрана — ответить на
// вопрос поддержки «что у человека сохранено», а не дать редактировать чужое.
// Открытие фиксируется в журнале на сервере, о чём прямо сказано в шапке.
import { useEffect, useState, type ReactElement } from 'react';
import { Modal } from '../ui/shell';
import { useT } from '../i18n';
import { segmentTitle } from '../core';
import { fetchUserState } from './api';
import type { AdminUser, UserStateView } from './api';
import styles from './UserStateModal.module.css';

export interface UserStateModalProps {
  user: AdminUser;
  onClose(): void;
}

export function UserStateModal({ user, onClose }: UserStateModalProps): ReactElement {
  const t = useT();
  const [view, setView] = useState<UserStateView | null>(null);
  const [failed, setFailed] = useState(false);
  const [raw, setRaw] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchUserState(user.id).then((res) => {
      if (cancelled) return;
      if (res.ok) setView(res.value);
      else setFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, [user.id]);

  const state = view?.state ?? null;
  const segments = state ? Object.values(state.library) : [];

  return (
    <Modal title={t('admin.view.title', { email: user.email })} onClose={onClose} wide>
      <div className={styles.body}>
        <p className={styles.warning}>{t('admin.view.readonly')}</p>

        {failed && <p className={styles.warning}>{t('auth.err.unknown')}</p>}
        {!view && !failed && <p className={styles.muted}>{t('auth.busy')}</p>}

        {view && !state && <p className={styles.muted}>{t('admin.view.empty')}</p>}

        {state && (
          <>
            <div className={styles.summary}>
              <span>
                {t('admin.view.variants')}: <b>{state.variants.length}</b>
              </span>
              <span>
                {t('admin.view.segments')}: <b>{segments.length}</b>
              </span>
              <span>
                {t('toolbar.travelers')}: <b>{state.travelers}</b>
              </span>
              <span>
                {t('toolbar.currency')}: <b>{state.displayCurrency}</b>
              </span>
            </div>

            <ul className={styles.variants}>
              {state.variants.map((variant) => (
                <li key={variant.id} className={styles.variant}>
                  <span className={styles.variantName}>{variant.name}</span>
                  <ol className={styles.segments}>
                    {variant.segmentIds.map((id) => {
                      const segment = state.library[id];
                      return (
                        <li key={id}>{segment ? segmentTitle(segment) : id}</li>
                      );
                    })}
                  </ol>
                </li>
              ))}
            </ul>

            <button type="button" className="btn btn-sm" onClick={() => setRaw((v) => !v)}>
              {t(raw ? 'admin.view.hideRaw' : 'admin.view.showRaw')}
            </button>
            {raw && <pre className={styles.raw}>{JSON.stringify(state, null, 2)}</pre>}
          </>
        )}
      </div>
    </Modal>
  );
}
